// src/utils/venueCharges.ts
//
// Single source of truth for the per-venue charges published by the backend in
// `venue.metadata`.
//
// These used to be invented client-side: a 0.25 security-deposit rate, an inline
// 10000 prefix/suffix rate and a 0.18 GST rate, all set in the venue mapper. That
// meant a rate revision on the backend never reached the quote — People's Plaza
// moved to 1,50,000/day and 11,000 prefix/suffix and the app kept billing the old
// numbers. Read the published values instead; do not reintroduce constants here.

import { Venue } from '../services/venues.api';

export interface VenueChargeMetadata {
  prefixSuffixRatePerDay?: number;
  securityDeposit?: number;
  securityDepositPercent?: number;
  gstPercent?: number;
}

/**
 * Anything carrying the API `metadata` blob. `fixedSecurityDeposit` is the
 * legacy name-matched override still applied by the venue mapper.
 */
type ChargeableVenue = Pick<Venue, 'metadata'> & { fixedSecurityDeposit?: number };

/**
 * GST applies to every venue, including the ones with no `metadata` row
 * published (Necklace Road, Sanjeevaiah Park, the inactive venues). Falling back
 * to 0 here would silently zero-rate them, so the statutory 18% is the default.
 */
const DEFAULT_GST_PERCENT = 18;

const chargeMetadata = (venue?: ChargeableVenue | null): VenueChargeMetadata =>
  (venue?.metadata ?? {}) as VenueChargeMetadata;

/**
 * Per-day charge for erection/dismantling (prefix and suffix) days.
 * `metadata: null` means the venue levies no prefix/suffix charge.
 */
export const getPrefixSuffixRatePerDay = (venue?: ChargeableVenue | null): number =>
  chargeMetadata(venue).prefixSuffixRatePerDay ?? 0;

/**
 * Deposit expressed as a percentage of the rent, when the venue publishes one.
 * Returns undefined for venues still on a flat deposit — callers use this only
 * to word the price breakdown, never to compute (see `getSecurityDeposit`).
 */
export const getSecurityDepositPercent = (
  venue?: ChargeableVenue | null
): number | undefined => {
  if (venue?.fixedSecurityDeposit != null) return undefined;
  return chargeMetadata(venue).securityDepositPercent;
};

/**
 * Security deposit for the booking, in rupees.
 *
 * Precedence: `fixedSecurityDeposit` (name override) → `securityDepositPercent`
 * (a proportion of `rentAmount`) → flat `securityDeposit` → 0.
 *
 * `rentAmount` is the venue rent for the quote (rate × booked days) BEFORE
 * prefix/suffix rent and GST — it is required so a percent venue can never be
 * quoted a 0 deposit by a caller that forgot to pass it.
 *
 * `fixedSecurityDeposit` still wins because Sanjeevaiah Park's 3,000 deposit has
 * no `metadata` published yet; dropping the override would quote it at 0. Once
 * the backend publishes metadata for it, the mapper's name-matching can go.
 */
export const getSecurityDeposit = (
  venue: ChargeableVenue | null | undefined,
  rentAmount: number
): number => {
  if (venue?.fixedSecurityDeposit != null) return venue.fixedSecurityDeposit;

  const metadata = chargeMetadata(venue);
  if (metadata.securityDepositPercent != null) {
    return rentAmount * (metadata.securityDepositPercent / 100);
  }

  return metadata.securityDeposit ?? 0;
};

/** GST as a multiplier, e.g. 0.18. Applied to rent + prefix/suffix rent only. */
export const getGstRate = (venue?: ChargeableVenue | null): number =>
  (chargeMetadata(venue).gstPercent ?? DEFAULT_GST_PERCENT) / 100;
